import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Paperclip, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { routes } from '@/shared/constants'
import { useOrgTicketAttachments } from './hooks/useOrgTicketAttachments'
import { useOrgTicketSystems } from './hooks/useOrgTicketCatalogs'
import { useOrgTicketScope } from './hooks/useOrgTicketScope'
import { useCreateOrgTicket } from './hooks/useOrgTicketMutations'

const TYPE_LABEL_KEY: Record<string, string> = {
  incidencia: 'tickets.form.typeIncidencia',
  propuesta: 'tickets.form.typePropuesta',
}

/**
 * Form de crear ticket (decisiones 5/8/9 del goal): tipo incidencia|propuesta,
 * negocio de la organización, sistema + campos obligatorios DINÁMICOS (la
 * misma fuente que valida el backend — 422 con `camposFaltantes`), título,
 * descripción y adjuntos. Los adjuntos se suben tras crear el ticket (el
 * contrato presigna contra un ticket existente) y al final navega al detalle.
 */
export function CreateTicketPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { systems, creatableTypes, loading: loadingSystems } =
    useOrgTicketSystems()
  // Los negocios creables salen del ALCANCE (no de portalMyBusinesses, que se
  // limita a los negocios de la organización del token): a un dueño de negocio
  // del mundo POS le llegan aquí sus negocios
  // (goal portal-tickets-alcance-por-membresia).
  const { scope } = useOrgTicketScope()
  const { createTicket, creating } = useCreateOrgTicket()
  const { uploadAttachment } = useOrgTicketAttachments()

  const [type, setType] = useState('')
  const [businessId, setBusinessId] = useState('')
  const [systemCode, setSystemCode] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [metadata, setMetadata] = useState<Record<string, string>>({})
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const system = useMemo(
    () => systems.find((s) => s.code === systemCode) ?? null,
    [systems, systemCode],
  )
  const requiredFields = useMemo(
    () =>
      [...(system?.fields ?? [])]
        .sort((a, b) => a.order - b.order)
        .filter((f) => f.required),
    [system],
  )

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    if (!type) next.type = t('tickets.form.requiredField')
    if (!businessId) next.businessId = t('tickets.form.requiredField')
    if (!systemCode) next.systemCode = t('tickets.form.requiredField')
    if (!title.trim()) next.title = t('tickets.form.requiredField')
    for (const f of requiredFields) {
      if (!metadata[f.name]?.trim()) {
        next[`meta:${f.name}`] = t('tickets.form.requiredField')
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // Crear → subir adjuntos contra el código nuevo (decisión 8: se presigna
  // contra un ticket existente; secuencial, un fallo no descarta los demás) →
  // navegar al detalle.
  const handleSubmit = async () => {
    if (!validate()) return
    try {
      const created = await createTicket({
        type,
        businessId,
        systemCode,
        title: title.trim(),
        description: description.trim() || undefined,
        metadataJson: JSON.stringify(metadata),
      })
      if (!created) throw new Error(t('common.error'))
      if (files.length) {
        setUploadingAttachments(true)
        for (const file of files) {
          try {
            await uploadAttachment(created.code, file)
          } catch {
            toast.error(`No se pudo subir ${file.name}`)
          }
        }
        setUploadingAttachments(false)
      }
      toast.success(`${t('tickets.form.created')}: ${created.code}`)
      navigate(routes.app.tickets.detail(created.code))
    } catch (e) {
      setUploadingAttachments(false)
      toast.error(e instanceof Error ? e.message : t('common.error'))
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="space-y-3">
        <Link
          to={routes.app.tickets.list}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" />
          {t('tickets.title')}
        </Link>
        <h1 className="font-serif text-[30px] font-normal leading-[1.15] tracking-tight text-ink">
          {t('tickets.newTicket')}
        </h1>
      </header>

      <Card className="space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('tickets.form.type')}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger aria-label={t('tickets.form.type')}>
                <SelectValue placeholder={t('tickets.form.type')} />
              </SelectTrigger>
              <SelectContent>
                {creatableTypes.map((ct) => (
                  <SelectItem key={ct} value={ct}>
                    {TYPE_LABEL_KEY[ct] ? t(TYPE_LABEL_KEY[ct]) : ct}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-xs text-red-600">{errors.type}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('tickets.form.business')}</Label>
            <Select value={businessId} onValueChange={setBusinessId}>
              <SelectTrigger aria-label={t('tickets.form.business')}>
                <SelectValue placeholder={t('tickets.form.business')} />
              </SelectTrigger>
              <SelectContent>
                {scope.businesses.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.businessId && (
              <p className="text-xs text-red-600">{errors.businessId}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t('tickets.form.system')}</Label>
          <Select
            value={systemCode}
            onValueChange={(v) => {
              setSystemCode(v)
              setMetadata({})
            }}
            disabled={loadingSystems}
          >
            <SelectTrigger aria-label={t('tickets.form.system')}>
              <SelectValue placeholder={t('tickets.form.system')} />
            </SelectTrigger>
            <SelectContent>
              {systems.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.systemCode && (
            <p className="text-xs text-red-600">{errors.systemCode}</p>
          )}
        </div>

        {/* Campos obligatorios DINÁMICOS del sistema elegido. */}
        {requiredFields.map((f) => (
          <div key={f.name} className="space-y-1.5">
            <Label htmlFor={`campo-${f.name}`}>{f.name} *</Label>
            <Input
              id={`campo-${f.name}`}
              value={metadata[f.name] ?? ''}
              onChange={(e) =>
                setMetadata((m) => ({ ...m, [f.name]: e.target.value }))
              }
            />
            {errors[`meta:${f.name}`] && (
              <p className="text-xs text-red-600">
                {errors[`meta:${f.name}`]}
              </p>
            )}
          </div>
        ))}

        <div className="space-y-1.5">
          <Label htmlFor="ticket-title">{t('tickets.form.title')}</Label>
          <Input
            id="ticket-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={300}
          />
          {errors.title && (
            <p className="text-xs text-red-600">{errors.title}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-description">
            {t('tickets.form.description')}
          </Label>
          <Textarea
            id="ticket-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t('tickets.form.attachments')}</Label>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const nuevos = Array.from(e.target.files ?? [])
              if (nuevos.length) setFiles((prev) => [...prev, ...nuevos])
              if (fileInput.current) fileInput.current.value = ''
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInput.current?.click()}
          >
            <Paperclip className="mr-1.5 size-4" />
            {t('tickets.form.attachments')}
          </Button>
          {files.length > 0 && (
            <ul className="space-y-1 pt-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 text-sm text-ink-muted"
                >
                  <Paperclip className="size-3.5" />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <button
                    type="button"
                    aria-label={`Quitar ${f.name}`}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, j) => j !== i))
                    }
                    className="text-ink-muted hover:text-ink"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button
            onClick={handleSubmit}
            disabled={creating || uploadingAttachments}
          >
            {(creating || uploadingAttachments) && (
              <Loader2 className="mr-1.5 size-4 animate-spin" />
            )}
            {t('tickets.form.submit')}
          </Button>
        </div>
      </Card>
    </div>
  )
}
